import {
  AccountCircleOutlined,
  HistoryRounded,
  LogoutRounded,
  MailOutlineRounded,
  ReceiptLongOutlined,
  SavingsOutlined,
} from '@mui/icons-material';
import { alpha, Box, Menu, MenuItem, Stack, Typography, type Theme } from '@mui/material';
import type { MouseEventHandler, ReactNode } from 'react';
import {
  getNeobetPlayerAccountMenuItems,
  NEOBET_PLAYER_ACCOUNT_MENU_ITEM,
  type NeobetPlayerAccountMenuItemKey,
} from '../../../../config/accountNavigation';
import { useAccountMenu } from '../../../../hooks/useAccountMenu';
import useBreakpoints from '../../../../hooks/useBreakpoints';
import useLocalization from '../../../../hooks/useLocalization';
import useProductAvailability from '../../../../hooks/useProductAvailability';
import { FormattedBalance } from '../../../atoms/FormattedBalance';
import UnreadMessagesBadge from '../../../atoms/UnreadMessagesBadge';
import NeobetAccountMenuButton from './AccountMenuButton';

type NeobetPlayerAccountMenuTriggerProps = {
  isOpen: boolean;
  onClick: MouseEventHandler<HTMLButtonElement>;
  showUnreadBadge: boolean;
};

type NeobetPlayerAccountMenuProps = {
  showUnreadBadge?: boolean;
  showSummary?: boolean;
  renderTrigger?: (props: NeobetPlayerAccountMenuTriggerProps) => ReactNode;
};

const menuIcons: Record<NeobetPlayerAccountMenuItemKey, ReactNode> = {
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.ACCOUNT]: <AccountCircleOutlined fontSize='small' />,
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MY_BETS]: <ReceiptLongOutlined fontSize='small' />,
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.HISTORY]: <HistoryRounded fontSize='small' />,
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.MESSAGES]: (
    <UnreadMessagesBadge>
      <MailOutlineRounded fontSize='small' />
    </UnreadMessagesBadge>
  ),
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.BONUS]: <SavingsOutlined fontSize='small' />,
  [NEOBET_PLAYER_ACCOUNT_MENU_ITEM.LOGOUT]: <LogoutRounded fontSize='small' />,
};

const styles = {
  menu: {
    mt: 0.25,
  },
  paper: (theme: Theme) => ({
    width: { xs: 256, md: 288 },
    borderRadius: 3,
    overflow: 'hidden',
    backgroundColor: 'neutral.700',
    border: '2px solid',
    borderColor: 'neutral.800',
    boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.32)}`,
  }),
  menuList: {
    p: 0,
  },
  wallet: {
    p: 1.75,
    m: 1.25,
    borderRadius: 3,
    backgroundColor: 'neutral.600',
  },
  walletHeading: {
    typography: 'caption',
    color: 'text.secondary',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
  },
  walletUsername: {
    typography: 'body2',
    fontWeight: 700,
    color: 'text.primary',
  },
  walletBalances: {
    mt: 1.25,
    gap: 0.75,
  },
  walletRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
  },
  walletRowLabel: {
    typography: 'body3',
    color: 'text.secondary',
  },
  walletRowValue: {
    typography: 'body2',
    fontWeight: 700,
    color: 'text.primary',
  },
  menuItem: {
    minHeight: 40,
    px: 0.75,
    py: 0.25,
    gap: 1,
    color: 'neutral.100',
    m: 1.5,
    borderRadius: 3,
    backgroundColor: 'neutral.600',
    borderBottom: 'none',
    '&:hover': {
      backgroundColor: 'neutral.500',
    },
  },
  menuItemIcon: {
    width: 36,
    height: 36,
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'neutral.300',
  },
  menuItemLabel: {
    typography: 'body2',
    fontWeight: 700,
    flex: 1,
  },
} as const;

const NeobetPlayerAccountMenu = ({ showUnreadBadge = false, renderTrigger }: NeobetPlayerAccountMenuProps) => {
  const { isMobile } = useBreakpoints();
  const { t } = useLocalization();
  const { isCasinoOnlyProduct } = useProductAvailability();
  const {
    anchorEl,
    isMenuOpen,
    preferredUsername,
    handleAccountMenuOpen,
    handleAccountMenuClose,
    handleLogoutClick,
    handleMenuItemClick,
  } = useAccountMenu();

  const menuItems = getNeobetPlayerAccountMenuItems(t, isMobile);
  const handleMenuItemAction = (item: (typeof menuItems)[number]) => {
    if (item.key === NEOBET_PLAYER_ACCOUNT_MENU_ITEM.LOGOUT) {
      handleLogoutClick();
      return;
    }

    handleMenuItemClick(item.path);
  };

  const trigger = renderTrigger ? (
    renderTrigger({ onClick: handleAccountMenuOpen, showUnreadBadge, isOpen: isMenuOpen })
  ) : (
    <NeobetAccountMenuButton isOpen={isMenuOpen} onClick={handleAccountMenuOpen} showUnreadBadge={showUnreadBadge} />
  );

  return (
    <>
      {trigger}
      <Menu
        data-testid='neobet-player-account-menu'
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleAccountMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        marginThreshold={0}
        sx={styles.menu}
        slotProps={{
          paper: {
            sx: styles.paper,
          },

          list: {
            disablePadding: true,
            sx: styles.menuList,
          },
        }}
      >
        <Box data-testid='neobet-player-account-wallet' sx={styles.wallet}>
          <Typography sx={styles.walletHeading}>{t('username')}</Typography>
          <Typography sx={styles.walletUsername} noWrap>
            {preferredUsername}
          </Typography>

          <Stack sx={styles.walletBalances}>
            <Box sx={styles.walletRow}>
              <Typography sx={styles.walletRowLabel}>{t('mainBalance')}</Typography>
              <Typography component='span' sx={styles.walletRowValue}>
                <FormattedBalance balanceType='main' />
              </Typography>
            </Box>

            {!isCasinoOnlyProduct && (
              <Box data-testid='neobet-player-account-wallet-bonus' sx={styles.walletRow}>
                <Typography sx={styles.walletRowLabel}>{t('bonus')}</Typography>
                <Typography component='span' sx={styles.walletRowValue}>
                  <FormattedBalance balanceType='bonus' />
                </Typography>
              </Box>
            )}
          </Stack>
        </Box>

        {menuItems.map((item) => (
          <MenuItem
            key={item.key}
            disableRipple
            data-testid={`neobet-player-account-item-${item.key}`}
            onClick={() => handleMenuItemAction(item)}
            sx={styles.menuItem}
          >
            <Box sx={styles.menuItemIcon}>{menuIcons[item.key]}</Box>
            <Typography sx={styles.menuItemLabel}>{item.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default NeobetPlayerAccountMenu;
